/**
 * PreviewSortLabel — a clickable Current/New/Diff column header for the Preview
 * tab. Shows a ↓/↑ arrow when it's the active sort column. View-only sort, so
 * it never changes the saved flow order.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { PreviewMetricSort, PreviewSortColumn } from '../preview-sort';

export interface PreviewSortLabelProps {
  column: PreviewSortColumn;
  label: string;
  sort: PreviewMetricSort | null;
  onCycle: (column: PreviewSortColumn) => void;
  className?: string;
}

const PreviewSortLabel: FC<PreviewSortLabelProps> = ({
  column,
  label,
  sort,
  onCycle,
  className,
}) => {
  const active = sort?.column === column;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onCycle(column);
      }}
      aria-label={`Sort by ${label}${active ? (sort?.dir === 'asc' ? ', ascending' : ', descending') : ''}`}
      className={cn(
        'flex items-center justify-end gap-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors',
        active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
        className,
      )}
    >
      <span>{label}</span>
      {active ? (
        sort?.dir === 'asc' ? (
          <ArrowUp className="h-2.5 w-2.5" aria-hidden />
        ) : (
          <ArrowDown className="h-2.5 w-2.5" aria-hidden />
        )
      ) : null}
    </button>
  );
};

export default PreviewSortLabel;
